import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../components/header/header";
import Footer from "../components/footer/footer";
import LogoCigna from "../../assets/img/logo-cigna.svg";
import LogoHumana from "../../assets/img/logo-humana.png";
import LogoMolina from "../../assets/img/logo-molina.svg";
import LogoUnited from "../../assets/img/logo-united.svg";
import LogoAetna from "../../assets/img/logo-aetna.svg";
import insurances from "./insurancesConfig";
import "../locationConditions/conditionLocationPage.scss";

const BOOKING_URL =
    "https://consumer.scheduling.athena.io/?locationId=20729-1&practitionerId=20729-1";

const SITE_ORIGIN = "https://www.virtualpsych.com";

// --- Helpers -------------------------------------------------------------
const upsertMeta = (selector, attrs) => {
    let tag = document.head.querySelector(selector);
    let created = false;
    if (!tag) {
        tag = document.createElement(attrs.tagName || "meta");
        Object.entries(attrs.create || {}).forEach(([k, v]) =>
            tag.setAttribute(k, v),
        );
        document.head.appendChild(tag);
        created = true;
    }
    const prev = {};
    Object.entries(attrs.set || {}).forEach(([k, v]) => {
        prev[k] = tag.getAttribute(k);
        tag.setAttribute(k, v);
    });
    return { tag, prev, created };
};

const upsertJsonLd = (id, data) => {
    let tag = document.head.querySelector(`script[data-seo-id="${id}"]`);
    const created = !tag;
    if (!tag) {
        tag = document.createElement("script");
        tag.type = "application/ld+json";
        tag.setAttribute("data-seo-id", id);
        document.head.appendChild(tag);
    }
    const prev = tag.textContent;
    tag.textContent = JSON.stringify(data);
    return { tag, prev, created };
};

const buildFaqs = ({ insurance, state }) => {
    return [
        {
            q: `Does ${insurance} cover online psychiatry in ${state}?`,
            a: `VirtualPsych is in-network with most major plans, and ${insurance} typically covers telepsychiatry visits the same way it covers in-person psychiatry. Exact coverage and copays depend on your specific ${insurance} plan, so we'll verify your benefits before your first visit.`,
        },
        {
            q: `How do I use my ${insurance} plan with VirtualPsych in ${state}?`,
            a: `Book an appointment online and add your ${insurance} member ID when you set up your account. Our team confirms your benefits in advance and lets you know your estimated cost before your visit.`,
        },
        {
            q: `Do I need a referral from my primary care doctor?`,
            a: `Most ${insurance} plans do not require a referral for telepsychiatry, but a small number do. If your plan requires one, we'll let you know during the booking process so you can request it.`,
        },
        {
            q: `What is my typical out-of-pocket cost with ${insurance}?`,
            a: `Costs vary by plan, but most ${insurance} members in ${state} pay a copay between $0 and $50 per session after their deductible. You'll get an estimate before you confirm your appointment.`,
        },
        {
            q: `Can a VirtualPsych psychiatrist prescribe medication using my ${insurance} plan in ${state}?`,
            a: `Yes. Our ${state}-licensed psychiatrists can evaluate, diagnose, and prescribe medication when clinically appropriate. Prescriptions are sent electronically to your preferred pharmacy and processed through your ${insurance} pharmacy benefits.`,
        },
    ];
};

// --- Component -----------------------------------------------------------
const InsuranceStatePage = ({ entry }) => {
    const { insurance, state, stateAbbr, slug } = entry;

    const title = `${insurance} Psychiatrists in ${state}`;
    const pageDescription = `Use your ${insurance} plan to see a licensed psychiatrist in ${state} online. Same-week telepsychiatry appointments, medication management, and ongoing care from VirtualPsych.`;
    const canonicalUrl = `${SITE_ORIGIN}/${slug}`;
    const faqs = buildFaqs({ insurance, state });

    // Related links: same insurance in other states, and other insurances in the same state
    const relatedStates = insurances
        .filter((l) => l.insurance === insurance && l.state !== state)
        .slice(0, 6);
    const relatedInsurances = insurances
        .filter((l) => l.state === state && l.insurance !== insurance)
        .slice(0, 6);

    useEffect(() => {
        const prevTitle = document.title;
        document.title = `${title} | VirtualPsych`;

        const cleanups = [];

        // Description
        cleanups.push(
            upsertMeta('meta[name="description"]', {
                create: { name: "description" },
                set: { content: pageDescription },
            }),
        );

        // Canonical
        cleanups.push(
            upsertMeta('link[rel="canonical"]', {
                tagName: "link",
                create: { rel: "canonical" },
                set: { href: canonicalUrl },
            }),
        );

        // Open Graph
        const og = [
            ["og:type", "website"],
            ["og:title", `${title} | VirtualPsych`],
            ["og:description", pageDescription],
            ["og:url", canonicalUrl],
            ["og:site_name", "VirtualPsych"],
            ["og:image", `${SITE_ORIGIN}/logo512.png`],
        ];
        og.forEach(([property, content]) => {
            cleanups.push(
                upsertMeta(`meta[property="${property}"]`, {
                    create: { property },
                    set: { content },
                }),
            );
        });

        // Twitter
        const tw = [
            ["twitter:card", "summary_large_image"],
            ["twitter:title", `${title} | VirtualPsych`],
            ["twitter:description", pageDescription],
            ["twitter:image", `${SITE_ORIGIN}/logo512.png`],
        ];
        tw.forEach(([name, content]) => {
            cleanups.push(
                upsertMeta(`meta[name="${name}"]`, {
                    create: { name },
                    set: { content },
                }),
            );
        });

        // Robots
        cleanups.push(
            upsertMeta('meta[name="robots"]', {
                create: { name: "robots" },
                set: { content: "index, follow, max-image-preview:large" },
            }),
        );

        // --- JSON-LD ---
        const medicalBusiness = {
            "@context": "https://schema.org",
            "@type": "MedicalBusiness",
            name: `VirtualPsych — ${insurance} Psychiatrists serving ${state}`,
            url: canonicalUrl,
            telephone: "+1-877-839-4824",
            medicalSpecialty: "Psychiatry",
            description: pageDescription,
            areaServed: {
                "@type": "State",
                name: state,
            },
            availableService: {
                "@type": "MedicalTherapy",
                name: `Online psychiatry covered by ${insurance}`,
                serviceType: "Telepsychiatry",
            },
            potentialAction: {
                "@type": "ReserveAction",
                target: BOOKING_URL,
                name: "Book an Appointment",
            },
        };

        const breadcrumb = {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_ORIGIN}/` },
                {
                    "@type": "ListItem",
                    position: 2,
                    name: `${insurance} Psychiatrists`,
                    item: canonicalUrl,
                },
                {
                    "@type": "ListItem",
                    position: 3,
                    name: state,
                    item: canonicalUrl,
                },
            ],
        };

        const faqPage = {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map(({ q, a }) => ({
                "@type": "Question",
                name: q,
                acceptedAnswer: { "@type": "Answer", text: a },
            })),
        };

        const ldCleanups = [
            upsertJsonLd("isp-medbusiness", medicalBusiness),
            upsertJsonLd("isp-breadcrumb", breadcrumb),
            upsertJsonLd("isp-faqpage", faqPage),
        ];

        return () => {
            document.title = prevTitle;
            cleanups.forEach(({ tag, prev, created }) => {
                if (created) {
                    tag.remove();
                } else {
                    Object.entries(prev).forEach(([k, v]) => {
                        if (v === null) tag.removeAttribute(k);
                        else tag.setAttribute(k, v);
                    });
                }
            });
            ldCleanups.forEach(({ tag, prev, created }) => {
                if (created) tag.remove();
                else tag.textContent = prev;
            });
        };
    }, [title, pageDescription, canonicalUrl, insurance, state, stateAbbr, faqs]);

    return (
        <div className="condition-location-page insurance-state-page">
            <Header />

            <main>
                <section className="clp-hero" aria-labelledby="isp-hero-title">
                    <div className="container">
                        <h1 id="isp-hero-title">{title}</h1>
                        <p className="clp-hero-sub">
                            Use your {insurance} plan to see a licensed psychiatrist in {state} online —
                            evaluation, treatment, and ongoing care from the comfort of home.
                        </p>
                        <a
                            href={BOOKING_URL}
                            className="button pri-button clp-cta"
                            style={{ color: "#fff" }}
                            aria-label={`Book an online psychiatry appointment in ${state} covered by ${insurance}`}
                        >
                            Book an Appointment
                        </a>
                    </div>
                </section>

                <section
                    className="clp-intro container"
                    aria-labelledby="isp-intro-title"
                >
                    <h2 id="isp-intro-title">
                        Online psychiatry in {state} with {insurance}
                    </h2>
                    <p>
                        VirtualPsych makes it easy for {state} residents to use their {insurance}{" "}
                        benefits to see a licensed psychiatrist online. Whether it's your first
                        psychiatric visit or you're switching providers, our {state}-licensed
                        clinicians offer personalized care, medication management, and follow-up —
                        all covered through your {insurance} plan.
                    </p>
                    <ul className="clp-list">
                        <li>In-network with {insurance} and most major plans</li>
                        <li>Same-week appointments available across {state}</li>
                        <li>Confidential video visits from anywhere</li>
                        <li>Prescriptions sent electronically to your pharmacy</li>
                    </ul>
                </section>

                <section
                    className="clp-steps"
                    aria-labelledby="isp-steps-title"
                >
                    <div className="container">
                        <h2 id="isp-steps-title">How it works with {insurance}</h2>
                        <ol className="clp-steps-list">
                            <li>
                                <span className="clp-step-num">1</span>
                                <span className="clp-step-text">
                                    Tell us about your needs and add your {insurance} member info.
                                </span>
                            </li>
                            <li>
                                <span className="clp-step-num">2</span>
                                <span className="clp-step-text">
                                    We verify your benefits and confirm your estimated copay.
                                </span>
                            </li>
                            <li>
                                <span className="clp-step-num">3</span>
                                <span className="clp-step-text">
                                    Meet your {state}-licensed psychiatrist by secure video.
                                </span>
                            </li>
                        </ol>
                    </div>
                </section>

                <div
                    className="home-insurance-strip"
                    aria-label="Insurance plans accepted"
                >
                    <div className="container">
                        <p className="home-insurance-title">
                            We accept most major insurances
                        </p>
                        <div className="home-insurance-logos">
                            <img src={LogoCigna} alt="Cigna insurance accepted" />
                            <img src={LogoHumana} alt="Humana insurance accepted" className="logo-humana" />
                            <img src={LogoMolina} alt="Molina Healthcare insurance accepted" className="logo-molina" />
                            <img src={LogoAetna} alt="Aetna insurance accepted" />
                            <img src={LogoUnited} alt="UnitedHealthcare insurance accepted" className="logo-united" />
                        </div>
                    </div>
                </div>

                <section
                    className="clp-faq"
                    aria-labelledby="isp-faq-title"
                >
                    <div className="container">
                        <h2 id="isp-faq-title">
                            Frequently asked questions about {insurance} psychiatry coverage in {state}
                        </h2>
                        <dl className="clp-faq-list">
                            {faqs.map(({ q, a }) => (
                                <div className="clp-faq-item" key={q}>
                                    <dt>{q}</dt>
                                    <dd>{a}</dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </section>

                <div className="clp-divider-band" aria-hidden="true" />

                {(relatedStates.length > 0 || relatedInsurances.length > 0) && (
                    <section
                        className="clp-related"
                        aria-labelledby="isp-related-title"
                    >
                        <div className="container">
                            <h2 id="isp-related-title">Explore related coverage</h2>
                            <div className="clp-related-grid">
                                {relatedStates.length > 0 && (
                                    <div>
                                        <h3>
                                            {insurance} psychiatrists in other states
                                        </h3>
                                        <ul>
                                            {relatedStates.map((l) => (
                                                <li key={l.slug}>
                                                    <Link to={`/${l.slug}`}>
                                                        {l.insurance} psychiatrists in {l.state}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {relatedInsurances.length > 0 && (
                                    <div>
                                        <h3>
                                            Other insurances accepted in {state}
                                        </h3>
                                        <ul>
                                            {relatedInsurances.map((l) => (
                                                <li key={l.slug}>
                                                    <Link to={`/${l.slug}`}>
                                                        {l.insurance} psychiatrists in {l.state}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                )}

                <section className="clp-cta-band" aria-labelledby="isp-cta-title">
                    <div className="container">
                        <h2 id="isp-cta-title">
                            Ready to book your {insurance} psychiatry visit?
                        </h2>
                        <p>
                            Schedule an online consultation with a {state}-licensed VirtualPsych
                            provider today.
                        </p>
                        <a
                            href={BOOKING_URL}
                            className="button pri-button"
                            style={{ color: "#fff" }}
                            aria-label={`Get started with an online psychiatry appointment in ${state} covered by ${insurance}`}
                        >
                            Get Started
                        </a>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default InsuranceStatePage;
